import { defineStore } from "pinia";
import { useGeneralStore } from "%/dynamic-component-editor/stores/general-store";
import { useCompletionStatusStore } from "%/dynamic-component-editor/stores/completion-status-store";
import { useStoryStore } from "%/dynamic-component-editor/stores/story-store";
import { Context } from "%/dynamic-component-editor/classes/context";
import ProvidesContextEntry from "%/dynamic-component-editor/classes/provides-context-entry";
import { STORY_ENTITY_TYPE } from "%/dynamic-component-editor/classes/platform-standard-entities";

const PROGRESS_RECORD_DELAY_MS = 2000;
const VIDEO_PLAYBACK_GRACE_MS = 15000;
const LAYOUT_GRACE_MS = 15000;
const URL_LAST_VIEWED_READ_STATUS = "/story/read-status/last-viewed";
// The reader arrives with this marker when they pressed Resume on another page;
// it is the only signal that we should move their scroll position for them.
// A FRAGMENT, not a query param: /story/{id} 302s to the story type's canonical
// nested URL and the redirect drops the query string, but the browser reapplies
// the fragment to the redirect target.
export const RESUME_URL_FRAGMENT = "resume";
// The DC tree mounts asynchronously, so the document has no height for a beat
// after arrival. Poll briefly rather than scrolling into a page that is not
// laid out yet (which lands at the bottom, or does nothing at all).
const RESUME_LAYOUT_POLL_MS = 150;
const RESUME_LAYOUT_ATTEMPTS = 40;
// The ambient context name the resume story registers under — the same name
// standardContextName() seeds pk-less at bootstrap, so this fills that entry
// in rather than adding a second one.
const RESUME_STORY_CONTEXT_NAME = ProvidesContextEntry.KEY_STANDARD_RESUME_STORY;

let scrollHandler = null;
let visibilityHandler = null;
let pageHideHandler = null;
let videoTimeUpdateHandler = null;
let recordTimer = null;
// One in-flight request per page load, shared by every caller (the contexts
// resolver and each element bound to the resume context).
let resumeStoryRequest = null;
let watchedSeconds = null;
let videoDurationSeconds = 0;
let trackingStartedAt = 0;

function computeScrollPercent() {
  const scrollable = document.documentElement.scrollHeight - window.innerHeight;
  if (scrollable <= 0) {
    return 100;
  }
  const percent = Math.round(window.scrollY / scrollable * 100);
  return Math.max(0, Math.min(100, percent));
}

function pageHasVideoElement() {
  return document.querySelector("video") !== null;
}

export const useReadStatusStore = defineStore("read-status-store", {
  state: () => ({
    percentByStory: {},
    resumeStoryId: 0,
    resumeStoryLoaded: false,
    trackingStoryId: 0,
    trackingMaxPercent: 0,
    videoMode: false,
  }),
  getters: {
    hasSignedInUser() {
      return useGeneralStore().isLoggedIn;
    },
  },
  actions: {
    // The story this viewer last opened, whether or not they finished it.
    // Idempotent: the first caller fetches, everyone else awaits that request.
    loadResumeStory() {
      if (resumeStoryRequest) {
        return resumeStoryRequest;
      }
      if (!this.hasSignedInUser) {
        this.resumeStoryLoaded = true;
        return Promise.resolve();
      }
      resumeStoryRequest = this.apiGet(
        URL_LAST_VIEWED_READ_STATUS,
        {},
        readStatus => {
          this.applyResumeStory(readStatus);
          this.resumeStoryLoaded = true;
        },
        () => {
          this.resumeStoryLoaded = true;
        },
      );
      return resumeStoryRequest;
    },
    // A viewer with no reading history gets an empty payload — leave the
    // context pk-less so every element bound to it renders nothing.
    applyResumeStory(readStatus) {
      const storyId = Number(readStatus?.story_id) || 0;
      if (!storyId) {
        return;
      }
      this.resumeStoryId = storyId;
      this.percentByStory[storyId] = Number(readStatus.percent_complete) || 0;
      Context.addContext(RESUME_STORY_CONTEXT_NAME, {
        ...Context.getContext(RESUME_STORY_CONTEXT_NAME),
        entityType: STORY_ENTITY_TYPE,
        entitySource: ProvidesContextEntry.KEY_STANDARD,
        standardEntity: ProvidesContextEntry.KEY_STANDARD_RESUME_STORY,
        entityPkUrlVars: { id: storyId },
      });
    },
    // Put the reader back where they stopped. Only ever called for an explicit
    // Resume press — arriving at a story normally should start at the top.
    // `readPercent` rides the story payload the reader page already fetches, and
    // getOrFetchStory dedupes in-flight requests, so resuming costs no request
    // of its own.
    restoreResumePosition(storyId) {
      if (!storyId || !this.hasSignedInUser) {
        return;
      }
      return useStoryStore().getOrFetchStory(storyId).then(story => {
        const percent = Number(story?.readPercent) || 0;
        if (percent <= 0) {
          return;
        }
        this.percentByStory[storyId] = percent;
        this.scrollToReadPercent(percent);
      });
    },
    scrollToReadPercent(percent, attempt = 0) {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) {
        if (attempt < RESUME_LAYOUT_ATTEMPTS) {
          setTimeout(() => this.scrollToReadPercent(percent, attempt + 1), RESUME_LAYOUT_POLL_MS);
        }
        return;
      }
      window.scrollTo({ top: Math.round(percent / 100 * scrollable), behavior: "smooth" });
    },
    recordReadStatus(storyId, percentComplete, secondsConsumed, durationSeconds) {
      if (!storyId || !this.hasSignedInUser) {
        return;
      }
      return this.apiPost(
        `/story/${storyId}/read-status`,
        {
          percent_complete: percentComplete,
          seconds_consumed: secondsConsumed,
          duration_seconds: durationSeconds,
        },
        readStatus => {
          this.percentByStory[storyId] = readStatus.percent_complete;
          const completion = useCompletionStatusStore();
          completion.applyCompletionStatus(storyId, readStatus.completion_status);
          completion.applyAffectedFolders(readStatus.affected_folders);
        },
        () => {},
      );
    },
    startStoryReadTracking(storyId) {
      if (!storyId || !this.hasSignedInUser) {
        return;
      }
      this.stopStoryReadTracking();
      this.trackingStoryId = storyId;

      watchedSeconds = new Set();
      videoDurationSeconds = 0;
      trackingStartedAt = Date.now();

      scrollHandler = () => this.onStoryScroll();
      videoTimeUpdateHandler = event => this.onVideoTimeUpdate(event);
      pageHideHandler = () => this.flushProgressRecord(true);
      visibilityHandler = () => {
        if (document.visibilityState === "hidden") {
          this.flushProgressRecord(true);
        }
      };

      window.addEventListener("scroll", scrollHandler, { passive: true });
      // media events do not bubble, but they are delivered during the capture phase,
      // so a capturing listener on document catches whichever <video> element is live
      // (the player re-creates the element when transcoding resolves).
      document.addEventListener("timeupdate", videoTimeUpdateHandler, true);
      window.addEventListener("pagehide", pageHideHandler);
      document.addEventListener("visibilitychange", visibilityHandler);

      this.queueProgressRecord();
    },
    onVideoTimeUpdate(event) {
      const video = event.target;
      if (!video || !watchedSeconds || !(video instanceof HTMLMediaElement)) {
        return;
      }
      const duration = Math.floor(video.duration || 0);
      if (duration <= 0) {
        return;
      }
      if (!this.videoMode) {
        this.videoMode = true;
        this.trackingMaxPercent = 0;
      }
      videoDurationSeconds = duration;
      watchedSeconds.add(Math.floor(video.currentTime));
      const percent = Math.round(watchedSeconds.size / duration * 100);
      const clamped = Math.max(0, Math.min(100, percent));
      if (clamped > this.trackingMaxPercent) {
        this.trackingMaxPercent = clamped;
        this.queueProgressRecord();
      }
    },
    onStoryScroll() {
      if (this.videoMode) {
        return;
      }
      const percent = computeScrollPercent();
      if (percent > this.trackingMaxPercent) {
        this.trackingMaxPercent = percent;
        this.queueProgressRecord();
      }
    },
    queueProgressRecord() {
      if (recordTimer) {
        clearTimeout(recordTimer);
      }
      recordTimer = setTimeout(() => this.flushProgressRecord(), PROGRESS_RECORD_DELAY_MS);
    },
    flushProgressRecord(isFinalRecord = false) {
      if (recordTimer) {
        clearTimeout(recordTimer);
        recordTimer = null;
      }
      if (!this.trackingStoryId) {
        return;
      }
      // A video on the page may not have been played yet. Hold off on scroll
      // progress so it cannot set a monotonic floor the real watch percentage
      // can never come back under — but only for as long as the grace window,
      // or a story the reader never plays would never record at all.
      if (!isFinalRecord && !this.videoMode && pageHasVideoElement()
        && Date.now() - trackingStartedAt < VIDEO_PLAYBACK_GRACE_MS) {
        this.queueProgressRecord();
        return;
      }
      // The reader page mounts its element tree asynchronously, so for the first
      // moments the document is shorter than the window — indistinguishable from
      // a fully-read page, which scored 100% and, being monotonic, marked the
      // story finished forever before it had rendered a word. Wait for a height
      // rather than banking that; a genuinely short page still scores once the
      // grace window closes.
      if (!isFinalRecord && !this.videoMode
        && document.documentElement.scrollHeight - window.innerHeight <= 0
        && Date.now() - trackingStartedAt < LAYOUT_GRACE_MS) {
        this.queueProgressRecord();
        return;
      }
      // Only measure while the page is still laid out. During teardown the
      // document collapses to zero scrollable height, which reads as 100%.
      if (!this.videoMode && !isFinalRecord) {
        this.trackingMaxPercent = Math.max(this.trackingMaxPercent, computeScrollPercent());
      }
      const secondsConsumed = watchedSeconds ? watchedSeconds.size : 0;
      const durationSeconds = this.videoMode ? videoDurationSeconds : 0;
      this.recordReadStatus(this.trackingStoryId, this.trackingMaxPercent, secondsConsumed, durationSeconds);
    },
    stopStoryReadTracking() {
      if (scrollHandler) {
        window.removeEventListener("scroll", scrollHandler);
        scrollHandler = null;
      }
      if (videoTimeUpdateHandler) {
        document.removeEventListener("timeupdate", videoTimeUpdateHandler, true);
        videoTimeUpdateHandler = null;
      }
      if (pageHideHandler) {
        window.removeEventListener("pagehide", pageHideHandler);
        pageHideHandler = null;
      }
      if (visibilityHandler) {
        document.removeEventListener("visibilitychange", visibilityHandler);
        visibilityHandler = null;
      }
      this.flushProgressRecord(true);
      this.trackingStoryId = 0;
      this.trackingMaxPercent = 0;
      this.videoMode = false;
      watchedSeconds = null;
      videoDurationSeconds = 0;
      trackingStartedAt = 0;
    },
  },
});
